import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getFollowUpTableDataKey,
  getNewlyTableDataKey,
  getPartialsTableDataKey,
  useTableFiltersMap,
} from 'infrastructure/hooks/ccm/console';
import { ModuleType } from 'infrastructure/enums';
import { getKeyNoteKey } from 'infrastructure/hooks/key-notes/use-get-key-note';

import type { IPatientInfo } from 'components/organisms/oneplaform/key-notes-drawer/types';
import type { QueryKey } from '@tanstack/react-query';
import type { TTableName, IKeyNoteData } from 'infrastructure/interfaces';
import type { TPatientInfo } from 'store/ccm/console';

type ITablesKeys = {
  [K in TTableName]: (filters: any) => QueryKey;
};
export const CCMConsoleTablesKeys: ITablesKeys = {
  'follow-up': getFollowUpTableDataKey,
  newly: getNewlyTableDataKey,
  partials: getPartialsTableDataKey,
};

export const useDrawer = () => {
  const { isOpenDrawer, tableNote } = useAppSelector(
    (state) => state.ccmConsole,
  );
  const queryClient = useQueryClient();
  const { ccmConsole: store } = useAppDispatch();

  const filters = useTableFiltersMap();

  const patientGuid = tableNote?.patientGuid ?? null;
  const tableName = tableNote?.tableName;

  const reloadTable = useCallback(async () => {
    if (!tableName || !patientGuid) return;
    queryClient.invalidateQueries(
      CCMConsoleTablesKeys[tableName](filters[tableName]),
    );
    const ccmNotesKey = getKeyNoteKey({
      patientGuid,
      module: ModuleType.CCM,
      showExpired: false,
    });

    await queryClient.refetchQueries(ccmNotesKey);
    const updatedNotes = queryClient.getQueryData<IKeyNoteData[]>(ccmNotesKey);
    if (updatedNotes) {
      store.updateQueueItem({
        guid: patientGuid,
        notes: updatedNotes,
      });
    }
  }, [filters, patientGuid, queryClient, store, tableName]);

  const handleCloseDrawer = useCallback(() => {
    store.toggleOpenDrawer();
  }, [store]);

  const fallbackPatientInfo = useCallback((): IPatientInfo | null => {
    if (!tableNote?.patientName || !patientGuid) return null;
    const parts = tableNote.patientName.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? '';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : firstName;

    return {
      guid: patientGuid,
      firstName,
      lastName,
      birthDate: '',
      language: '',
      doctorName: '',
    };
  }, [patientGuid, tableNote?.patientName]);

  const mapStorePatientInfo = useCallback(
    (info: TPatientInfo): IPatientInfo => ({
      guid: info.guid,
      firstName: info.firstName,
      lastName: info.lastName,
      birthDate: info.birthDate ?? '',
      language: info.language ?? '',
      doctorName: info.doctorName ?? '',
    }),
    [],
  );

  const patientInfo = useMemo<IPatientInfo | null>(() => {
    if (!patientGuid) return null;
    if (tableNote?.patientInfo) {
      return mapStorePatientInfo(tableNote.patientInfo);
    }
    return fallbackPatientInfo();
  }, [
    fallbackPatientInfo,
    mapStorePatientInfo,
    patientGuid,
    tableNote?.patientInfo,
  ]);

  return {
    handleCloseDrawer,
    patientGuid,
    isOpenDrawer,
    patientInfo,
    handleNoteSaved: reloadTable,
  };
};
